// Voice input session — powers the shared VoiceOverlay (mic buttons on the
// canvas prompt composer + the assistant sidebar composer). Owns the
// microphone (getUserMedia → AnalyserNode, live level for the overlay
// visuals) and the v2 server engines (architecture/VOICE_COMMANDS.md):
//
//   STT — MediaRecorder (webm/opus) → `sdk.voiceTranscribe` (Groq Whisper on
//         the API server). No browser recognition engine exists anymore.
//   TTS — `sdk.voiceTts` (ElevenLabs on the API server), a raw streaming
//         Response played back through one primed Audio element so the reply
//         starts before the clip finishes downloading.
//
// v1's Web Speech / speechSynthesis engines are REMOVED (2026-08-06 per Nika:
// "do not use browser at all — use 11labs"). There is NO browser-speech
// fallback: when the server voice routes answer 503 (secrets unprovisioned)
// the overlay shows a clear "Voice backend not provisioned" notice and stops
// gracefully instead of degrading to a robotic browser voice.
//
// Every runtime handle lives on the VoiceOverlay element's `scope` and is torn
// down by the overlay's onBeforeRemove via voiceTeardown — no module-mutable
// state.
//
// FA204/FA207 / frank trap — every private engine helper this file used to
// declare at module scope (`_overlayOf`, `_rootOf`, `_voiceOf`, `_up`,
// `_teardown`, `_speak`, `_startEngine`, … 30+ of them, deeply
// cross-referencing each other) read as `undefined` once this file was
// frank.toJSON'd for platform delivery: each exported `el.call` handler below
// is serialized independently, so a free reference to a module-scope sibling
// — even another EXPORT referenced bare — has nothing to resolve to post-
// serialization (project_frank_drops_module_local_helpers; the exact same
// class the `_causeChain`/`_isTokenRejection` pair in bootShell.js hit).
//
// Fix: the whole engine is genuinely ONE piece of state machinery keyed off a
// single VoiceOverlay instance, so it now lives in THAT component's
// `scope: {}` (components/VoiceOverlay.js) — the same construct
// `syncFocus`/`syncChatSuppression` already use there, and the same one
// AppOrgSwitcher.js's `rawOrgs`/`activeOrg` pair uses for cross-calling scope
// methods. Every export below reaches it via `overlay.scope.<name>(...)`.
// `_overlayOf` is the one exception — it's how a caller GETS to
// `overlay.scope` in the first place, so it can't live on the thing it
// resolves; it stays a tiny self-contained local, duplicated in every export
// (FA207). `appendVoiceTurn`/`talkUnavailableCopy` stay exported for their
// own unit tests (voiceSession.test.js) — their logic is duplicated inline at
// the internal call sites that used to bare-call them (now
// `VoiceOverlay.scope.turn`/`.talkUnavailable`) rather than routing an
// internal call through the export.
//
// Root-state contract (every write carries { preventFetch: true }):
//   voiceOpen      overlay visible
//   voiceMinimized talk posture — overlay collapsed to the composer-width card
//   voiceMode      'command' | 'talk'
//   voiceChatKey   thread prefix the log renders ('' | '_ax_' | '_db_')
//   voiceLang      BCP-47 locale override ('' = browser locale; drives the
//                  conversational agent's language override — Whisper
//                  auto-detects, so transcription needs no hint)
//   voiceStatus    'idle' | 'listening' | 'thinking' | 'speaking' | 'error' |
//                  'waiting' | 'choosing' (the loop holds: a step waits for
//                  the person's OK, or a question for their pick —
//                  VoiceOverlay.scope.holdForAsk)
//   voiceInterim   live partial transcript (server engines produce none — the
//                  key stays so a future engine can light it up again)
//   voiceFinal     last committed transcript
//   voiceResult    short outcome label ("Zoomed to fit") — state only; the
//                  TEXT of every answer lives in voiceTurns, the one thread
//   voiceError     human-readable failure copy
//   voiceHostReady set by VoiceOverlay onInit — mic buttons render only then
//   voiceStartedAt the call clock's zero (ms) — set by voiceStart, 0 when closed
//
// Two modes, ONE streamlined pipeline each (2026-08-06 per Nika — "command is
// one off - talk is continuous"):
//
//   command — ONE-OFF. press → record (silence auto-commits, PTT release
//             commits) → transcribe → execute (host grammar via
//             el.call('voiceProjectCommand'), else the assistant) → answer
//             (text into the one thread + the 11labs voice) → done: the
//             session closes itself. Never re-arms.
//
//   talk    — CONTINUOUS conversation. Engine 'agent' (Nika 2026-10-26:
//             "workspace voice mode should have voice assistant just like we
//             have on fly"; "11labs talks, Simone acts"): a live ElevenLabs
//             voice agent hears and talks in real time, and every workspace
//             question goes to Simone, every "show me" to Fly, as client
//             tools (VoiceOverlay.scope.startAgentEngine). Where the agent is
//             not available, engine 'simone' — the SAME server pipeline run
//             as a loop (VOICE-TALK-ONLY-SIMONE-1 part 3, route A): record
//             (silence auto-commits) → transcribe → Simone (sendPrompt → the
//             sdk.ai transport) → speak ONLY the final turn text → record …
//             Either way the session persists across turns until the user
//             ends it with ✕/Esc (or the agent ends the call on a goodbye).
//
// Answers render in ONE surface: the voiceTurns thread (VoiceLog), shared by
// every posture. Status lines say only where the lifecycle is — they never
// carry the answer text (the old second section).

import { isOwnerOrAdmin } from './appAccess.js'

// Resolves the VoiceOverlay instance from any calling element. Cannot live in
// VoiceOverlay's own `scope` (see the frank-trap note above) — it's how a
// caller gets TO that scope in the first place. Duplicated verbatim inline in
// every export below (FA207 — a bare module-scope reference to a shared copy
// would be the exact same trap it exists to avoid).

// THE canonical transcript primitive. Pure (new array out, never mutated in
// place — a v3.14 signal needs a fresh reference to see the change), so it is
// exported and unit-tested. Upserts by `id`: a streaming agent turn keeps the
// same id and is rewritten as its text grows, which is what preserves
// token-by-token streaming through a state-backed log.
//
// Entry shape — the contract every voice surface renders:
//   { id, role: 'user' | 'agent', text, kind?, streaming? }
//     kind      'action' for an executed canvas command; ABSENT (or 'speech')
//               for spoken/typed conversation. Surfaces should treat a missing
//               kind as speech so older entries keep rendering.
//     streaming true only while an agent turn is still growing.
//
// The engine's OWN use of this upsert lives as an inline copy in
// VoiceOverlay.scope.turn — see the file-header frank-trap note.
export const appendVoiceTurn = (turns, entry, cap) => {
  const list = Array.isArray(turns) ? turns : []
  if (!entry || !entry.id) return list
  const max = cap || 50
  const at = list.findIndex((t) => t && t.id === entry.id)
  const next = at === -1 ? list.concat([entry]) : list.slice()
  if (at !== -1) next[at] = { ...next[at], ...entry }
  return next.length > max ? next.slice(next.length - max) : next
}

// ——— engine switch ———

// Talk REQUIRES the conversational agent — there is no other talk engine.
// Say exactly why it is unavailable instead of silently swapping (the silent
// fallback era made "why is the voice robotic?" unanswerable).
//
// The `no-agent` leg is role-aware (VOICE-CONVAI engineering half, split from
// tickets/opus.md 2026-08-11): "No ElevenLabs agent is configured for this
// workspace — talk is unavailable" named a vendor and stopped, telling
// nobody who could fix it or how. An admin can act on it now (they own the
// flags page); anyone else can only ask, so they're told who. Uses
// `isOwnerOrAdmin` (appAccess.js, same package) rather than a fourth copy of
// the claim check. Pulled out to a pure function (reason, root) → string —
// exported for the unit test, same pattern as convaiLangFor/convaiMixBins in
// voiceConvai.js.
//
// Since part 3 of VOICE-TALK-ONLY-SIMONE-1 talk no longer runs on the agent,
// so the engine no longer reaches this copy (VoiceControls.engineWhy still
// explains a leftover `voiceEngineReason` with the same words).
// @testOnly — exported for voiceSession.test.js; no live code imports or calls it.
export const talkUnavailableCopy = (reason, root) => {
  if (reason === 'unsupported-lang') {
    return 'The ElevenLabs agent does not speak this language — pick another to talk.'
  }
  if (reason === 'offline')
    return 'You are offline — talk needs a connection to the ElevenLabs agent.'
  if (reason === 'start-failed') return 'Could not reach the ElevenLabs agent — try again.'
  return isOwnerOrAdmin(root)
    ? 'Voice needs an ElevenLabs agent. Configure it in /admin/flags.'
    : "Voice isn't set up for this workspace yet — contact your administrator."
}

// ——— public el.call surface ———

export const voiceStart = function (opts) {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  sc.primeAudio(overlay)
  const o = opts || {}
  const v = sc.voiceOf(overlay)
  const root = sc.rootOf(overlay)
  // WAI-ARIA dialog focus contract, capture half (see VoiceOverlay.scope's
  // `restoreFocus` for the release half). `this` is the control the user
  // actually pressed — VoiceBtn's onPointerdown / the chat composer's own mic
  // button — never the overlay: voicePttPress/voiceToggle forward their own
  // captured `this` through unchanged, so this reads correctly from the PTT-
  // hold path (the 280ms deferred call closes over the same element), the
  // plain-click path, and a direct voiceStart call alike. Skipped when
  // `this` already IS the overlay (nothing meaningful to restore to).
  if (this && this !== overlay && this.node) v.focusOrigin = this.node
  v.active = true
  v.closeAfterCommit = !!o.ptt
  v.awaiting = null
  v.chatScope = o.scope || 'general'
  sc.up(overlay, {
    voiceOpen: true,
    // voiceHost re-assert: a hosting composer writes root.voiceHost INSIDE
    // the mic gesture, immediately before this runs — re-writing whatever
    // is there makes the open bundle the single authoritative write, and
    // (with the teardown null below) any opener that did NOT set a host
    // this gesture opens hostless instead of inheriting a stale composer.
    voiceHost: (root && root.voiceHost) || null,
    // Every session opens on the full surface; a dispatched turn collapses it.
    voiceMinimized: false,
    // VOICE-TALK-ONLY-SIMONE-1 part 2: TALK is the default. Only an
    // EXPLICIT `mode: 'command'` (the composer's push-to-talk HOLD — a
    // one-shot spoken command while held) still opens the one-shot
    // pipeline; every plain open is a conversation. The visible
    // Command/Talk switcher is unmounted (VoiceStrip/VoiceOverlay/
    // ReasoningPill) — voiceSetMode stays exported for programmatic use.
    voiceMode: o.mode === 'command' ? 'command' : 'talk',
    // Which thread the log renders — the same prefix sendPrompt namespaces by.
    voiceChatKey: sc.chatPrefix(v.chatScope),
    voiceStatus: 'listening',
    voiceInterim: '',
    voiceFinal: '',
    voiceResult: '',
    voiceError: '',
    voiceReply: '',
    // Every session opens with a hot mic — mute is a per-session choice.
    voiceMicMuted: false,
    // The call clock's zero (VoiceCallClock) — every open is a new call.
    voiceStartedAt: Date.now(),
    // voiceTurns is deliberately NOT cleared here. The island is ONE running
    // thread you can scroll back through (2026-08-03 per Nika "logging in one
    // small minimized thread so you can keep track"), and command mode is a
    // one-shot — it tears the session down after every exchange, so wiping on
    // open meant the previous exchange vanished the moment you spoke again.
    // appendVoiceTurn's cap bounds the history instead.
    voiceEngine: null,
    // Recomputed by startEngine immediately below; cleared first so a stale
    // reason from the last session can never describe this one.
    voiceEngineReason: null
  })
  sc.startEngine(overlay)
}

export const voiceStop = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (overlay) overlay.scope.teardown(overlay)
}

export const voiceToggle = function (opts) {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const root = overlay.scope.rootOf(overlay)
  if (root.voiceOpen) overlay.scope.teardown(overlay)
  else this.call('voiceStart', opts)
}

export const voiceSetMode = function (mode) {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  const root = sc.rootOf(overlay)
  if (root.voiceMode === mode) return
  v.awaiting = null
  clearInterval(v.turnPoll)
  v.turnPoll = null
  // Command needs the full surface (there is no conversation to sit beside).
  sc.up(overlay, {
    voiceMode: mode === 'talk' ? 'talk' : 'command',
    voiceMinimized: mode === 'talk' ? root.voiceMinimized : false
  })
  // The engine follows the mode: leaving talk shuts the conversational
  // session down; entering talk hands capture to the agent. Either direction
  // discards the in-flight utterance/reply — a mode switch is a fresh start.
  if (v.active) {
    sc.stopSpeaking(overlay)
    if (mode === 'talk') sc.stopRecorder(v)
    sc.up(overlay, { voiceStatus: 'listening', voiceInterim: '', voiceError: '', voiceResult: '' })
    sc.startEngine(overlay)
  }
}

// Bar → call screen, from the Expand button of either bar. Releasing the host
// is what lets the screen take over from the composer strip (the screen shows
// only while no composer hosts); on the island the host is already null.
export const voiceExpand = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (overlay) overlay.scope.up(overlay, { voiceMinimized: false, voiceHost: null })
}

// Call screen → the island: the call keeps running, the workspace comes back.
// The screen's Minimize button, its Type action and Escape on the screen.
export const voiceMinimize = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const root = overlay.scope.rootOf(overlay)
  if (!root || !root.voiceOpen) return
  overlay.scope.up(overlay, { voiceMinimized: true, voiceHost: null })
}

// Put the caret back in a composer after a call control gave the text input
// back (VoiceControls Type, VoiceCallActions Type). The field: the composer
// whose strip the control sits in — its Textarea is the strip's sibling in both
// hosts (CanvasPromptTextarea, the assistant drawer's ComposerWrap) — else the
// global composer. Call it AFTER the posture write: the field un-hides in that
// update's microtask, and this focus is queued behind it.
export const voiceFocusComposer = function () {
  const el = this
  if (!el) return
  const strip = typeof el.lookup === 'function' ? el.lookup('VoiceStrip') : null
  const shell = typeof el.getRoot === 'function' ? el.getRoot() : null
  const composer =
    (strip && strip.parent) ||
    (shell && shell.lookdown ? shell.lookdown('CanvasPromptTextarea') : null)
  const field =
    composer && (composer.Textarea || (composer.lookdown && composer.lookdown('Textarea')))
  queueMicrotask(() => {
    const node = field && field.node
    if (node && node.isConnected && typeof node.focus === 'function') node.focus()
  })
}

// The call chrome's 60 fps half (components/VoiceControls.js): the orb's ring
// and the call clock of ONE host. Each posture's host calls it from its own
// `onFrame` after checking that IT is the surface on show, so exactly one runs
// per frame. The ring is a style write (setNodeStyles — never state); the clock
// writes its OWN state once a second, never the root. Returns the live mic
// level (0..1) so a host can drive more from the same read (the call screen's
// edge glow).
export const voiceCallFrame = function () {
  const host = this
  const root = host && host.state && (host.state.root || host.state)
  if (!root || !root.voiceOpen || !host.node) return 0
  // Refs resolved once per host and dropped when their node leaves the
  // document — the frame loop must never walk the tree on every tick.
  const live = (ref) => !!(ref && ref.node && ref.node.isConnected)
  let refs = host.__voiceCallRefs
  if (!refs || !live(refs.ring)) {
    const shell = typeof host.getRoot === 'function' ? host.getRoot() : null
    refs = {
      ring: host.lookdown ? host.lookdown('VoiceOrbRing') : null,
      clock: host.lookdown ? host.lookdown('VoiceCallClock') : null,
      overlay:
        host.scope && host.scope.__isVoiceOverlay
          ? host
          : shell && shell.lookdown
            ? shell.lookdown('VoiceOverlay')
            : null,
      motion:
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia('(prefers-reduced-motion: reduce)')
          : null,
      t: 0,
      scale: 1
    }
    host.__voiceCallRefs = refs
  }

  const started = Number(root.voiceStartedAt) || 0
  const clock = refs.clock
  if (started && clock && clock.state && typeof clock.state.update === 'function') {
    const secs = Math.max(0, Math.floor((Date.now() - started) / 1000))
    if (clock.state.secs !== secs) clock.state.update({ secs })
  }

  const status = root.voiceStatus
  const v = (refs.overlay && refs.overlay.scope && refs.overlay.scope.voice) || {}
  let level = 0
  if (status === 'listening' && !root.voiceMicMuted && v.analyser && v.bins) {
    v.analyser.getByteFrequencyData(v.bins)
    let sum = 0
    for (let i = 0; i < v.bins.length; i++) sum += v.bins[i]
    level = sum / (v.bins.length * 255)
  }

  const ring = refs.ring
  if (!ring || typeof ring.setNodeStyles !== 'function') return level
  refs.t += 1 / 60
  const t = refs.t
  let target = 1
  let opacity = 0.14
  if (!(refs.motion && refs.motion.matches)) {
    if (status === 'speaking') {
      target = 1.12 + 0.14 * Math.abs(Math.sin(t * 3.2))
      opacity = 0.22
    } else if (status === 'thinking' || status === 'connecting') {
      target = 1.06 + 0.05 * Math.sin(t * 2.2)
      opacity = 0.16
    } else if (status === 'listening' && !root.voiceMicMuted) {
      target = 1.04 + Math.min(0.46, level * 2.6)
      opacity = 0.16 + Math.min(0.22, level * 1.4)
    }
  }
  // Eased toward the target so a spiky spectrum reads as breathing, not jitter.
  refs.scale += (target - refs.scale) * 0.28
  ring.setNodeStyles({
    transform: 'scale(' + refs.scale.toFixed(3) + ')',
    opacity: opacity.toFixed(3)
  })
  return level
}

// Push-to-talk: pointerdown arms a hold timer; a quick release is a plain
// toggle click, a hold starts a one-shot session committed on release.
export const voicePttPress = function (opts) {
  const el = this
  const overlayOf = (n) => {
    if (n && n.scope && n.scope.__isVoiceOverlay) return n
    const root = n && n.getRoot ? n.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  // Prime here too: this runs INSIDE the pointerdown, whereas voiceStart is
  // reached from the 280ms hold timer — outside the gesture.
  const overlay = overlayOf(el)
  if (overlay) overlay.scope.primeAudio(overlay)
  clearTimeout(el.scope._vpttTimer)
  el.scope._vpttHeld = false
  el.scope._vpttTimer = setTimeout(() => {
    el.scope._vpttHeld = true
    el.call('voiceStart', { ...(opts || {}), ptt: true })
  }, 280)
}

export const voicePttRelease = function (opts) {
  const el = this
  clearTimeout(el.scope._vpttTimer)
  el.scope._vpttTimer = null
  if (el.scope._vpttHeld) {
    el.scope._vpttHeld = false
    el.call('voiceCommitAndClose')
  } else {
    el.call('voiceToggle', opts)
  }
}

// Pointer cancelled mid-gesture (scroll/drag takeover) — never toggles: a
// pending hold is dropped, an active hold commits like a release.
export const voicePttCancel = function () {
  const el = this
  clearTimeout(el.scope._vpttTimer)
  el.scope._vpttTimer = null
  if (el.scope._vpttHeld) {
    el.scope._vpttHeld = false
    el.call('voiceCommitAndClose')
  }
}

// Graceful PTT finish — stop the recorder, transcribe what was held, execute,
// answer, close. If nothing was said the pipeline closes quietly.
export const voiceCommitAndClose = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  v.closeAfterCommit = true
  // A CONVERSATION is never closed by letting go of the mic — talk ends only
  // on ✕/Esc (the documented contract): the live agent and the SIMONE loop.
  if (v.engine === 'simone' || v.engine === 'agent') return
  if (v.recorder && v.recorder.state !== 'inactive') {
    sc.finishRecording(overlay)
    return
  }
  clearTimeout(v.stopTimer)
  // Backstop: released before the recorder armed (mic permission prompt
  // still up, engine still starting) — close once nothing is in flight.
  v.stopTimer = setTimeout(() => {
    const root = sc.rootOf(overlay)
    if (v.active && !v.awaiting && root.voiceStatus === 'listening') sc.teardown(overlay)
  }, 1400)
}

// Wired from VoiceOverlay.onStateUpdate — watches the async turns a commit
// started: assistant reply (chatTyping true→false) and freestyle builds.
// Deliberately reads the LIVE root rather than the `changes` patch — the
// patch only carries the keys a given writer touched. Idempotent, so both the
// onStateUpdate hook and the backstop poll can call it freely.
export const voiceOnStateChange = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  if (!v.active || !v.awaiting) return
  const root = sc.rootOf(overlay)
  if (!root) return

  // The live agent's ask_workspace turn (VoiceOverlay.scope.agentAsk): the
  // same thread, read the same honest way, but the answer goes back to the
  // agent as the tool's result (VoiceOverlay.scope.agentSettle) — the agent
  // says it in its own words. Nothing is spoken here, and the streamed text
  // stays in the thread: the captions carry what the agent says.
  if (v.awaiting.type === 'chat' && v.awaiting.tool) {
    const turn = v.awaiting
    const msgs = root[turn.k + 'chatMessages'] || []
    const typing = !!root[turn.k + 'chatTyping']
    // The ask in the thread: the newest user row with its words, after the
    // length the thread had when it was sent. Until it lands, nothing can be
    // its answer.
    let at = -1
    for (let i = msgs.length - 1; i >= (turn.since || 0); i--) {
      const m = msgs[i]
      if (m && m.role === 'user' && String(m.text || '').trim() === turn.tool.request) {
        at = i
        break
      }
    }
    const after = at === -1 ? [] : msgs.slice(at + 1)
    const last = after[after.length - 1]
    if (!typing && last && last.role === 'system' && String(last.text || '').trim()) {
      sc.agentSettle(overlay, { error: String(last.text).trim() })
      return
    }
    if (
      !typing &&
      last &&
      last.role === 'assistant' &&
      last.metadata &&
      last.metadata.kind === 'error'
    ) {
      sc.agentSettle(overlay, {
        error:
          String(last.metadata.errorMessage || last.text || '').trim() || 'Simone could not answer.'
      })
      return
    }
    // An ask of this request waits on the person — a step for the OK, a
    // question for a pick: the agent hears it at once, as the tool's result
    // (VoiceOverlay.scope.agentWaits) — never the narration before it as the
    // answer (the typing flag may be off), never "did not answer in time"
    // while the person decides. The answer on the screen resumes the request
    // on a fresh clock (askAnswered).
    const held = sc.heldAsk(after, overlay)
    if (held) {
      sc.agentWaits(overlay, held)
      return
    }
    if (turn.waitStep) sc.askAnswered(overlay)
    if (turn.startedAt && Date.now() - turn.startedAt > 120000) {
      sc.agentSettle(overlay, { error: 'Simone did not answer in time.' })
      return
    }
    if (typing || !last || last.role !== 'assistant' || !String(last.text || '').trim()) return
    sc.agentSettle(overlay, { text: String(last.text) })
    return
  }

  if (v.awaiting.type === 'chat') {
    const k = v.awaiting.k
    const typing = !!root[k + 'chatTyping']
    const msgs = root[k + 'chatMessages'] || []
    const last = msgs[msgs.length - 1]
    // The voice is saying that an ask waits on the person: nothing talks
    // over it — when it ends, VoiceOverlay.scope.noticeSaid reads the thread
    // again.
    if (v.awaiting.notice) return
    // Honest end states — the loop must never wedge in 'thinking', and
    // nothing but Simone's FINAL answer is ever voiced:
    //   • the transport pushed a role:'system' row (no bridge / no
    //     workspace / AI service not ready) and stopped typing → error;
    //   • the answer row carries `metadata.kind === 'error'` (the server's
    //     failWorkspaceLoop synthesized it — credits exhausted, provider
    //     down) → error with the server's message, not spoken;
    //   • nothing resolved inside TURN_TIMEOUT_MS → error.
    // voiceRetry re-arms the engine from any of these.
    const TURN_TIMEOUT_MS = 120000
    const endTurn = (msg) => {
      v.awaiting = null
      clearInterval(v.turnPoll)
      v.turnPoll = null
      sc.fail(overlay, msg)
    }
    if (!typing && last && last.role === 'system' && String(last.text || '').trim()) {
      endTurn(String(last.text).trim())
      return
    }
    if (
      !typing &&
      last &&
      last.role === 'assistant' &&
      last.metadata &&
      last.metadata.kind === 'error'
    ) {
      endTurn(
        String(last.metadata.errorMessage || last.text || '').trim() ||
          'Simone could not answer — retry.'
      )
      return
    }
    // An ask of this turn waits on the person — a step the client gate
    // parked for the OK, or a question ask_user put on the screen — and the
    // server turn waits on it: the voice says so once and the loop holds —
    // VoiceOverlay.scope.holdForAsk. The turn clock stops while it waits (the
    // time on the screen is the person's), and the narration before it is
    // never the answer (the typing flag may be off). The answer on the screen
    // resumes the turn on a fresh clock (askAnswered). Only rows after this
    // turn's send count: an older ask is this send's to supersede.
    const held = sc.heldAsk(msgs.slice(v.awaiting.since || 0), overlay)
    if (!held && v.awaiting.waitStep) sc.askAnswered(overlay)
    if (!held && typing && v.turnStartedAt && Date.now() - v.turnStartedAt > TURN_TIMEOUT_MS) {
      endTurn('Simone did not answer in time — retry.')
      return
    }
    // Mirror the in-flight assistant text into the canonical transcript on
    // every tick — BEFORE the typing bail below, because that is exactly when
    // streaming happens. Same turnId each time, so the transcript upsert
    // rewrites the entry in place rather than stacking partial copies. The
    // log shows the stream; the VOICE waits for the final text. Nothing
    // streams while an ask waits.
    if (v.awaiting.turnId && last && last.role === 'assistant') {
      const streamed = String(last.text || '')
      if (streamed.trim()) {
        sc.turn(overlay, {
          id: v.awaiting.turnId,
          role: 'agent',
          text: streamed,
          streaming: typing && !held
        })
      }
    }
    if (held) {
      sc.holdForAsk(overlay, held)
      return
    }
    if (typing) return
    if (!last || last.role !== 'assistant' || !String(last.text || '').trim()) return
    v.awaiting = null
    clearInterval(v.turnPoll)
    v.turnPoll = null
    // Crude but effective markdown → speakable text: drop code blocks,
    // inline markup and links; long replies are capped so the readout stays
    // conversational (and bounds the TTS spend per turn). Local — used only
    // here.
    const speakable = (text) => {
      const t = (text || '')
        .replace(/```[\s\S]*?```/g, ' code block omitted. ')
        .replace(/`([^`]*)`/g, '$1')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/[#*_>~|]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return t.length > 600 ? t.slice(0, 597) + '…' : t
    }
    const reply = speakable(last.text)
    if (reply) sc.speak(overlay, reply)
    else sc.flashDone(overlay, 'Sent')
  } else if (v.awaiting.type === 'freestyle') {
    if (root.freestyleBusy) return
    v.awaiting = null
    clearInterval(v.turnPoll)
    v.turnPoll = null
    const built = root.freestyleError ? 'Build failed' : 'Built on canvas'
    sc.actionTurn(overlay, built)
    sc.flashDone(overlay, built)
  }
}

// Halt the spoken reply on the ACTIVE engine (the stop-speaking button — the
// talk loop's barge-in). On the one-shot engine the answer was already
// delivered as text, so the cut finishes the exchange; on the SIMONE loop
// afterSpeak goes straight back to listening; the live agent owns its own cut
// (VoiceOverlay.scope.agentStopSpeaking — the call goes on).
export const voiceStopSpeaking = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  if (!v.active) return
  const root = sc.rootOf(overlay)
  if (!root || root.voiceStatus !== 'speaking') return
  if (v.engine === 'agent') {
    sc.agentStopSpeaking(overlay)
    return
  }
  sc.stopSpeaking(overlay)
  sc.afterSpeak(overlay)
}

// Input-axis mic mute — flips root.voiceMicMuted WITHOUT ending the session
// or touching posture (the island mic control; CloseBtn owns ending). Output
// is a separate axis: a muted mic never suppresses the agent's `speaking`.
// Engine-dispatched — the UI only ever calls this and reads voiceMicMuted,
// never learning which engine muted.
export const voiceMicToggle = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  if (!v.active) return
  const root = sc.rootOf(overlay)
  if (!root || !root.voiceOpen) return
  const muted = !root.voiceMicMuted
  // The live agent holds the mic itself: it stops hearing, the call goes on.
  if (v.engine === 'agent') {
    sc.agentMicMuted(overlay, muted)
    sc.up(overlay, muted ? { voiceMicMuted: true, voiceInterim: '' } : { voiceMicMuted: false })
    return
  }
  if (muted) {
    // Pause capture but keep tracks + analyser — the wave stays live and
    // unmute is a plain resume.
    clearInterval(v.silenceLoop)
    v.silenceLoop = null
    if (v.recorder && v.recorder.state === 'recording') {
      try {
        v.recorder.pause()
      } catch (_) {}
    }
  } else if (v.recorder && v.recorder.state === 'paused') {
    try {
      v.recorder.resume()
    } catch (_) {}
    // Fresh silence clock — a stale one would insta-commit on resume.
    v.lastSpeechAt = Date.now()
    clearInterval(v.silenceLoop)
    v.silenceLoop = setInterval(() => sc.watchSilence(overlay), 150)
  } else if (!v.recorder && root.voiceStatus === 'listening') {
    sc.record(overlay)
  }
  const patch = { voiceMicMuted: muted }
  // A half-heard interim would sit stale under a muted mic.
  if (muted) patch.voiceInterim = ''
  sc.up(overlay, patch)
}

// Append a transcript turn from ANOTHER engine module. voiceSession owns the
// voiceTurns contract (id allocation, cap, upsert), so the conversational
// engine reaches it through el.call rather than a sibling import (FA001).
// Passing an explicit `id` rewrites that turn in place — how a streaming
// reply grows without stacking partial copies. role 'action' logs an executed
// command (renders as an outcome row, not speech).
export const voiceAddTurn = function (role, text, id) {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const body = String(text || '')
  if (!body.trim()) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  let turnId = id
  if (!turnId) {
    v.turnSeq = (v.turnSeq || 0) + 1
    turnId = (role === 'user' ? 'u' : 'a') + v.turnSeq
  }
  const entry = { id: turnId, role: role === 'user' ? 'user' : 'agent', text: body }
  if (role === 'action') entry.kind = 'action'
  sc.turn(overlay, entry)
}

// Recover from the `error` status without ending the session — including the
// unprovisioned-backend and agent-unavailable stops. Re-runs the engine
// switch for the current mode.
export const voiceRetry = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  if (!v.active) return
  v.awaiting = null
  sc.up(overlay, {
    voiceError: '',
    voiceStatus: 'listening',
    voiceInterim: '',
    voiceResult: ''
  })
  sc.startEngine(overlay)
}

// Mid-session conversational drop (the line dropped while the session is
// open): surface the drop and keep the surface alive so retry can reconnect;
// talk still only ENDS on ✕/Esc. The live agent reports its own drops
// (VoiceOverlay.scope.agentEnded); this stays the el.call for any other
// engine module.
export const voiceEngineFallback = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay) return
  const sc = overlay.scope
  const v = sc.voiceOf(overlay)
  if (!v.active) return
  sc.up(overlay, { voiceEngineReason: 'start-failed' })
  sc.fail(overlay, 'shell.voice.error.agentDisconnected')
}

// Overlay unmount hook — releases mic/engine/playback without touching state.
export const voiceTeardown = function () {
  const overlayOf = (el) => {
    if (el && el.scope && el.scope.__isVoiceOverlay) return el
    const root = el && el.getRoot ? el.getRoot() : null
    return root && root.lookdown ? root.lookdown('VoiceOverlay') : null
  }
  const overlay = overlayOf(this)
  if (!overlay || !overlay.scope || !overlay.scope.voice) return
  const sc = overlay.scope
  const v = sc.voice
  v.active = false
  sc.stopAgent(overlay)
  clearTimeout(v.doneTimer)
  clearTimeout(v.stopTimer)
  clearInterval(v.turnPoll)
  v.turnPoll = null
  sc.stopRecorder(v)
  sc.stopSpeaking(overlay)
  sc.stopTracks(v)
  v.engine = null
  // The overlay can unmount with a session still open — the canvas one is
  // gated `if: !_inShell`, so a standalone↔shell transition strands the UI
  // "open" against an engine that no longer exists (mic buttons stuck active,
  // a surface that can never be closed). Releasing the handles without
  // releasing the state is only half a teardown.
  const root = sc.rootOf(overlay)
  if (root && root.update && root.voiceOpen) {
    root.update(
      {
        voiceOpen: false,
        voiceMinimized: false,
        voiceHost: null,
        voiceStatus: 'idle',
        voiceInterim: '',
        voiceEngine: null,
        voiceStartedAt: 0
      },
      { preventFetch: true }
    )
  }
  // Same dialog-close focus restore as a normal teardown — an unmount is
  // still a session end from the user's point of view. Defensive by
  // construction (VoiceOverlay.scope.restoreFocus checks `isConnected`), so
  // this is a safe no-op on the standalone↔shell transition where the origin
  // control is itself mid-unmount.
  sc.restoreFocus(v)
}
